// Browser-safe mirrors of prisma/schema.prisma enums. The type-only Prisma import is
// erased at build time, and each `satisfies` clause fails compilation if the schema drifts.
import type {
  AccessLevel as PrismaAccessLevel,
  AccountUsageIntent as PrismaAccountUsageIntent,
  ContentPartyType as PrismaContentPartyType,
  ContentType as PrismaContentType,
  Domain as PrismaDomain,
  EventProvider as PrismaEventProvider,
  EventResponseStatus as PrismaEventResponseStatus,
  FileTextCacheReason as PrismaFileTextCacheReason,
  EventStatus as PrismaEventStatus,
  ExternalRefProvider as PrismaExternalRefProvider,
  ExternalRefResourceType as PrismaExternalRefResourceType,
  ExternalProvider as PrismaExternalProvider,
  MessageDirection as PrismaMessageDirection,
  MessageExtractKind as PrismaMessageExtractKind,
  MessageExtractOutcome as PrismaMessageExtractOutcome,
  MessageExtractStatus as PrismaMessageExtractStatus,
  NeoInputMode as PrismaNeoInputMode,
  OwnerScope as PrismaOwnerScope,
  PartyResolutionStatus as PrismaPartyResolutionStatus,
  PartyType as PrismaPartyType,
  ProcessExecutionKind as PrismaProcessExecutionKind,
  ProcessRunStatus as PrismaProcessRunStatus,
  ProcessStatus as PrismaProcessStatus,
  ProcessTriggerType as PrismaProcessTriggerType,
  ProvenanceEntryStatus as PrismaProvenanceEntryStatus,
  CollectionKind as PrismaCollectionKind,
  TemplateStatus as PrismaTemplateStatus,
  TemplateLayout as PrismaTemplateLayout,
  CaptureFlow as PrismaCaptureFlow,
  ProvenanceType as PrismaProvenanceType,
  SubscriptionStatus as PrismaSubscriptionStatus,
  TransmittalRecipientType as PrismaTransmittalRecipientType,
  TransmittalType as PrismaTransmittalType,
  WalletEntryKind as PrismaWalletEntryKind,
} from '@prisma/client';

export const AccessLevel = {
  VIEWER: 'VIEWER',
  COMMENTER: 'COMMENTER',
  EDITOR: 'EDITOR',
  OWNER: 'OWNER',
  CUSTODIAN: 'CUSTODIAN',
} as const satisfies { [K in PrismaAccessLevel]: K };
export type AccessLevel = (typeof AccessLevel)[keyof typeof AccessLevel];

export const AccountUsageIntent = {
  PERSONAL: 'PERSONAL',
  WORK: 'WORK',
  PERSONAL_AND_WORK: 'PERSONAL_AND_WORK',
} as const satisfies { [K in PrismaAccountUsageIntent]: K };
export type AccountUsageIntent = (typeof AccountUsageIntent)[keyof typeof AccountUsageIntent];

export const ContentPartyType = {
  AUTHOR: 'AUTHOR',
  OWNER: 'OWNER',
  ASSIGNEE: 'ASSIGNEE',
  REVIEWER: 'REVIEWER',
  APPROVER: 'APPROVER',
  CONTRIBUTOR: 'CONTRIBUTOR',
  STAKEHOLDER: 'STAKEHOLDER',
  SUBJECT: 'SUBJECT',
  RELATED: 'RELATED',
  OBSERVER: 'OBSERVER',
  UNKNOWN: 'UNKNOWN',
} as const satisfies { [K in PrismaContentPartyType]: K };
export type ContentPartyType = (typeof ContentPartyType)[keyof typeof ContentPartyType];

export const ContentType = {
  ANCHOR: 'ANCHOR',
  DOCUMENT: 'DOCUMENT',
  EVENT: 'EVENT',
  MESSAGE: 'MESSAGE',
  PHOTO: 'PHOTO',
  RECORDING: 'RECORDING',
  VIDEO: 'VIDEO',
  FILE: 'FILE',
  URL: 'URL',
  MESSAGE_EXTRACT: 'MESSAGE_EXTRACT',
  NEO_CHAT: 'NEO_CHAT',
  PROCESS_RUN: 'PROCESS_RUN',
  MANUAL_SESSION: 'MANUAL_SESSION',
  COLLECTION: 'COLLECTION',
  TEMPLATE: 'TEMPLATE',
} as const satisfies { [K in PrismaContentType]: K };
export type ContentType = (typeof ContentType)[keyof typeof ContentType];

export const Domain = {
  CONSTRUCTION: 'CONSTRUCTION',
  MEDICAL: 'MEDICAL',
} as const satisfies { [K in PrismaDomain]: K };
export type Domain = (typeof Domain)[keyof typeof Domain];

export const NeoInputMode = {
  VOICE: 'VOICE',
  DICTATION: 'DICTATION',
  TEXT: 'TEXT',
} as const satisfies { [K in PrismaNeoInputMode]: K };
export type NeoInputMode = (typeof NeoInputMode)[keyof typeof NeoInputMode];

export const EventProvider = {
  GOOGLE_CALENDAR: 'GOOGLE_CALENDAR',
  VIVPORT: 'VIVPORT',
  MICROSOFT_CALENDAR: 'MICROSOFT_CALENDAR',
} as const satisfies { [K in PrismaEventProvider]: K };
export type EventProvider = (typeof EventProvider)[keyof typeof EventProvider];

export const EventResponseStatus = {
  NEEDS_ACTION: 'NEEDS_ACTION',
  ACCEPTED: 'ACCEPTED',
  DECLINED: 'DECLINED',
  TENTATIVE: 'TENTATIVE',
} as const satisfies { [K in PrismaEventResponseStatus]: K };
export type EventResponseStatus = (typeof EventResponseStatus)[keyof typeof EventResponseStatus];

export const EventStatus = {
  CONFIRMED: 'CONFIRMED',
  TENTATIVE: 'TENTATIVE',
  CANCELLED: 'CANCELLED',
} as const satisfies { [K in PrismaEventStatus]: K };
export type EventStatus = (typeof EventStatus)[keyof typeof EventStatus];

export const ExternalProvider = {
  GOOGLE: 'GOOGLE',
  MICROSOFT: 'MICROSOFT',
} as const satisfies { [K in PrismaExternalProvider]: K };
export type ExternalProvider = (typeof ExternalProvider)[keyof typeof ExternalProvider];

export const MessageDirection = {
  INBOUND: 'INBOUND',
  OUTBOUND: 'OUTBOUND',
  INTERNAL: 'INTERNAL',
} as const satisfies { [K in PrismaMessageDirection]: K };
export type MessageDirection = (typeof MessageDirection)[keyof typeof MessageDirection];

export const MessageExtractKind = {
  DECISION: 'DECISION',
  FAULT: 'FAULT',
  ACTION: 'ACTION',
  QUESTION: 'QUESTION',
  RISK: 'RISK',
} as const satisfies { [K in PrismaMessageExtractKind]: K };
export type MessageExtractKind = (typeof MessageExtractKind)[keyof typeof MessageExtractKind];

export const MessageExtractOutcome = {
  AFFIRMED: 'AFFIRMED',
  DECLINED: 'DECLINED',
  MITIGATED: 'MITIGATED',
  ACCEPTED: 'ACCEPTED',
} as const satisfies { [K in PrismaMessageExtractOutcome]: K };
export type MessageExtractOutcome = (typeof MessageExtractOutcome)[keyof typeof MessageExtractOutcome];

export const MessageExtractStatus = {
  OPEN: 'OPEN',
  CLARIFICATION: 'CLARIFICATION',
  RESOLVED: 'RESOLVED',
  CANCELLED: 'CANCELLED',
} as const satisfies { [K in PrismaMessageExtractStatus]: K };
export type MessageExtractStatus = (typeof MessageExtractStatus)[keyof typeof MessageExtractStatus];

export const OwnerScope = {
  MEMBER: 'MEMBER',
  HUB: 'HUB',
  SYSTEM: 'SYSTEM',
} as const satisfies { [K in PrismaOwnerScope]: K };
export type OwnerScope = (typeof OwnerScope)[keyof typeof OwnerScope];

export const PartyResolutionStatus = {
  UNRESOLVED: 'UNRESOLVED',
  LINKED_INTERNAL: 'LINKED_INTERNAL',
  VERIFIED_EXTERNAL: 'VERIFIED_EXTERNAL',
} as const satisfies { [K in PrismaPartyResolutionStatus]: K };
export type PartyResolutionStatus = (typeof PartyResolutionStatus)[keyof typeof PartyResolutionStatus];

export const PartyType = {
  PERSON: 'PERSON',
  SHARED_MAILBOX: 'SHARED_MAILBOX',
  MAILING_LIST: 'MAILING_LIST',
  RESOURCE: 'RESOURCE',
  ALIAS: 'ALIAS',
  ROLE_ACCOUNT: 'ROLE_ACCOUNT',
  ORGANIZATION: 'ORGANIZATION',
  SYSTEM: 'SYSTEM',
  UNKNOWN: 'UNKNOWN',
} as const satisfies { [K in PrismaPartyType]: K };
export type PartyType = (typeof PartyType)[keyof typeof PartyType];

export const ProcessExecutionKind = {
  AGENTIC: 'AGENTIC',
  DETERMINISTIC: 'DETERMINISTIC',
} as const satisfies { [K in PrismaProcessExecutionKind]: K };
export type ProcessExecutionKind = (typeof ProcessExecutionKind)[keyof typeof ProcessExecutionKind];

export const ProcessRunStatus = {
  QUEUED: 'QUEUED',
  RUNNING: 'RUNNING',
  WAITING_APPROVAL: 'WAITING_APPROVAL',
  SUCCEEDED: 'SUCCEEDED',
  FAILED: 'FAILED',
  SKIPPED: 'SKIPPED',
  DEAD_LETTER: 'DEAD_LETTER',
  CANCELLED: 'CANCELLED',
} as const satisfies { [K in PrismaProcessRunStatus]: K };
export type ProcessRunStatus = (typeof ProcessRunStatus)[keyof typeof ProcessRunStatus];

export const ProcessStatus = {
  ENABLED: 'ENABLED',
  DISABLED: 'DISABLED',
} as const satisfies { [K in PrismaProcessStatus]: K };
export type ProcessStatus = (typeof ProcessStatus)[keyof typeof ProcessStatus];

export const ProcessTriggerType = {
  TAG_APPLIED: 'TAG_APPLIED',
  FILE_UPLOADED: 'FILE_UPLOADED',
  SCHEDULE: 'SCHEDULE',
  EMAIL_RECEIVED: 'EMAIL_RECEIVED',
  MESSAGE_IMPORTED: 'MESSAGE_IMPORTED',
  MESSAGE_TRIAGE: 'MESSAGE_TRIAGE',
  EXTRACT_SCORING: 'EXTRACT_SCORING',
  OBLIGATION_EXTRACTION: 'OBLIGATION_EXTRACTION',
  MESSAGE_SYNC: 'MESSAGE_SYNC',
  CALENDAR_IMPORTED: 'CALENDAR_IMPORTED',
  DOCUMENT_UPDATED: 'DOCUMENT_UPDATED',
  DRIVE_SYNC: 'DRIVE_SYNC',
  DRIVE_TEXT_EXTRACTION: 'DRIVE_TEXT_EXTRACTION',
  XERO_EVENT: 'XERO_EVENT',
  DRIVE_PUBLISH: 'DRIVE_PUBLISH',
} as const satisfies { [K in PrismaProcessTriggerType]: K };
export type ProcessTriggerType = (typeof ProcessTriggerType)[keyof typeof ProcessTriggerType];

export const ProvenanceEntryStatus = {
  INFO: 'INFO',
  SUCCEEDED: 'SUCCEEDED',
  FAILED: 'FAILED',
} as const satisfies { [K in PrismaProvenanceEntryStatus]: K };
export type ProvenanceEntryStatus = (typeof ProvenanceEntryStatus)[keyof typeof ProvenanceEntryStatus];

export const CollectionKind = {
  OBSERVATION: 'OBSERVATION',
  ALBUM: 'ALBUM',
} as const satisfies { [K in PrismaCollectionKind]: K };
export type CollectionKind = (typeof CollectionKind)[keyof typeof CollectionKind];

export const TemplateStatus = {
  DRAFT: 'DRAFT',
  PUBLISHED: 'PUBLISHED',
  RETIRED: 'RETIRED',
} as const satisfies { [K in PrismaTemplateStatus]: K };
export type TemplateStatus = (typeof TemplateStatus)[keyof typeof TemplateStatus];

export const TemplateLayout = {
  SECTIONED: 'SECTIONED',
  LETTER: 'LETTER',
} as const satisfies { [K in PrismaTemplateLayout]: K };
export type TemplateLayout = (typeof TemplateLayout)[keyof typeof TemplateLayout];

export const CaptureFlow = {
  EVIDENCE_FIRST: 'EVIDENCE_FIRST',
  IMMEDIATE: 'IMMEDIATE',
} as const satisfies { [K in PrismaCaptureFlow]: K };
export type CaptureFlow = (typeof CaptureFlow)[keyof typeof CaptureFlow];

export const ProvenanceType = {
  NEO_CHAT: 'NEO_CHAT',
  PROCESS_RUN: 'PROCESS_RUN',
  MANUAL_SESSION: 'MANUAL_SESSION',
} as const satisfies { [K in PrismaProvenanceType]: K };
export type ProvenanceType = (typeof ProvenanceType)[keyof typeof ProvenanceType];

export const SubscriptionStatus = {
  TRIALING: 'TRIALING',
  ACTIVE: 'ACTIVE',
  PAST_DUE: 'PAST_DUE',
  CANCELED: 'CANCELED',
  INCOMPLETE: 'INCOMPLETE',
  INCOMPLETE_EXPIRED: 'INCOMPLETE_EXPIRED',
  UNPAID: 'UNPAID',
  PAUSED: 'PAUSED',
} as const satisfies { [K in PrismaSubscriptionStatus]: K };
export type SubscriptionStatus = (typeof SubscriptionStatus)[keyof typeof SubscriptionStatus];

export const TransmittalRecipientType = {
  TO: 'TO',
  CC: 'CC',
  BCC: 'BCC',
  REPLY_TO: 'REPLY_TO',
  ATTENDEE: 'ATTENDEE',
  OPTIONAL_ATTENDEE: 'OPTIONAL_ATTENDEE',
  REQUIRED_ATTENDEE: 'REQUIRED_ATTENDEE',
} as const satisfies { [K in PrismaTransmittalRecipientType]: K };
export type TransmittalRecipientType = (typeof TransmittalRecipientType)[keyof typeof TransmittalRecipientType];

export const TransmittalType = {
  EMAIL: 'EMAIL',
  CALENDAR_INVITE: 'CALENDAR_INVITE',
  INTERNAL_SHARE: 'INTERNAL_SHARE',
  PROCESS_OUTPUT: 'PROCESS_OUTPUT',
  SYSTEM_NOTIFICATION: 'SYSTEM_NOTIFICATION',
} as const satisfies { [K in PrismaTransmittalType]: K };
export type TransmittalType = (typeof TransmittalType)[keyof typeof TransmittalType];

export const WalletEntryKind = {
  TOPUP: 'TOPUP',
  USAGE_DEBIT: 'USAGE_DEBIT',
  ADJUSTMENT: 'ADJUSTMENT',
  REFUND: 'REFUND',
  PROMO_GRANT: 'PROMO_GRANT',
} as const satisfies { [K in PrismaWalletEntryKind]: K };
export type WalletEntryKind = (typeof WalletEntryKind)[keyof typeof WalletEntryKind];

export const FileTextCacheReason = {
  OK: 'OK',
  UNSUPPORTED_TYPE: 'UNSUPPORTED_TYPE',
  TOO_LARGE: 'TOO_LARGE',
  NO_TEXT_FOUND: 'NO_TEXT_FOUND',
} as const satisfies { [K in PrismaFileTextCacheReason]: K };
export type FileTextCacheReason = (typeof FileTextCacheReason)[keyof typeof FileTextCacheReason];

export const ExternalRefProvider = {
  XERO: 'XERO',
} as const satisfies { [K in PrismaExternalRefProvider]: K };
export type ExternalRefProvider = (typeof ExternalRefProvider)[keyof typeof ExternalRefProvider];

export const ExternalRefResourceType = {
  INVOICE: 'INVOICE',
  CONTACT: 'CONTACT',
} as const satisfies { [K in PrismaExternalRefResourceType]: K };
export type ExternalRefResourceType = (typeof ExternalRefResourceType)[keyof typeof ExternalRefResourceType];
